import axios, { AxiosResponse, AxiosInstance, AxiosError } from 'axios';
import NetworkError, { TNetworkInsideErrors } from 'shared/exceptions/network';
import { getAuthUserData } from 'shared/helpers/auth';
import { logout } from 'shared/helpers/auth/logout';
import { isRequestCanceled } from 'shared/utils/isRequestCanceled';

const buildNoStoreUrl = (
  baseURL: string,
  url: string,
  params: object,
): string => {
  const normalizedBase = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
  const requestUrl = new URL(url.replace(/^\//, ''), normalizedBase);

  Object.entries({ ...params, _: Date.now() }).forEach(([key, value]) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return;
    }

    requestUrl.searchParams.set(key, String(value));
  });

  return requestUrl.toString();
};

const rethrowIfCanceled = (error: unknown): void => {
  if (!isRequestCanceled(error)) {
    return;
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new DOMException('The operation was aborted.', 'AbortError');
};

export type TAxios = Promise<AxiosResponse>;

class Network {
  instance: AxiosInstance;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: Map<string, any>;

  constructor(baseURL: string, timeout: number) {
    this.instance = axios.create({
      baseURL,
      timeout,
    });

    this.methods = new Map([
      ['post', this.instance.post],
      ['get', this.instance.get],
      ['put', this.instance.put],
      ['patch', this.instance.patch],
      ['delete', this.instance.delete],
    ]);
  }

  public setToken(token: string | null): Network {
    this.instance.defaults.headers.Authorization = `Bearer ${token}`;
    return this;
  }

  public setSellerId(sellerId: string | null): Network {
    this.instance.defaults.headers.SellerId = sellerId;

    return this;
  }

  public async send<T>(
    requestType: string,
    url: string,
    payload: unknown = {},
    headers: unknown = {},
  ): Promise<T> {
    const token = getAuthUserData();

    if (token) {
      this.setToken(token);
    } else {
      this.setToken(null);
    }

    try {
      const method = this.methods.get(requestType);
      const data = await method(url, payload, headers);
      return data;
    } catch (error) {
      rethrowIfCanceled(error);

      const { response, message: errorMessage } = error as AxiosError<
        { message: string; errors: TNetworkInsideErrors | null },
        string
      >;
      if (response) {
        const {
          data: { message, errors },
          status,
        } = response;
        if (
          status === 401 &&
          message &&
          message !== 'The given data was invalid.'
        ) {
          logout('401');
        }

        throw new NetworkError(message, status, errors as null | undefined);
      } else {
        throw new NetworkError(errorMessage);
      }
    }
  }

  public async sendGetNoStore<T>(url: string, params: object = {}): Promise<T> {
    const token = getAuthUserData();
    const headers = new Headers({
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
    });

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    try {
      const response = await fetch(
        buildNoStoreUrl(
          String(this.instance.defaults.baseURL || ''),
          url,
          params,
        ),
        {
          method: 'GET',
          cache: 'no-store',
          headers,
        },
      );
      const body = (await response.json().catch(() => null)) as {
        message?: string;
        errors?: TNetworkInsideErrors | null;
      } | null;

      if (!response.ok) {
        const message = body?.message || response.statusText;

        if (
          response.status === 401 &&
          message &&
          message !== 'The given data was invalid.'
        ) {
          logout('401');
        }

        throw new NetworkError(
          message,
          response.status,
          (body?.errors ?? null) as null | undefined,
        );
      }

      return { data: body } as T;
    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }

      rethrowIfCanceled(error);
      const message = error instanceof Error ? error.message : 'Network error';
      throw new NetworkError(message);
    }
  }

  public async sendFile(
    requestType: string,
    url: string,
    payload: unknown = {},
    headers: unknown = {},
  ): Promise<TAxios> {
    const token = getAuthUserData();

    if (token) {
      this.setToken(token);
    } else {
      this.setToken(null);
    }
    try {
      const method = this.methods.get(requestType);
      const response = await method(url, payload, headers);
      return response;
    } catch (error) {
      rethrowIfCanceled(error);

      const { response, message: errorMessage } = error as AxiosError<
        { message: string; errors: TNetworkInsideErrors | null },
        string
      >;
      if (response) {
        const {
          data: { message, errors },
          status,
        } = response;
        throw new NetworkError(message, status, errors as null | undefined);
      } else {
        throw new NetworkError(errorMessage);
      }
    }
  }
}

export default Network;
